import type { Theme } from "../types/theme.js";
import { DEFAULT_THEME } from "./default-theme.js";

export function isTokenRef(value: unknown): value is `$${string}` {
  return typeof value === "string" && value.startsWith("$") && value.length > 1;
}

/**
 * Resolves a theme token such as `$colors.primary`, `$typography.headingFont`,
 * `$table.headerBackground` or `$logo`. Returns `undefined` when the token does
 * not exist in either the theme or the default theme.
 */
export function resolveToken(token: string, theme: Theme): string | undefined {
  const path = token.slice(1).split(".");
  const fromTheme = walk(theme, path);
  if (typeof fromTheme === "string") return fromTheme;
  const fromDefault = walk(DEFAULT_THEME, path);
  return typeof fromDefault === "string" ? fromDefault : undefined;
}

function walk(obj: unknown, path: string[]): unknown {
  let cur: unknown = obj;
  for (const p of path) {
    if (cur === null || cur === undefined || typeof cur !== "object") return undefined;
    if (!Object.prototype.hasOwnProperty.call(cur, p)) return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/** Returns a concrete value: token references resolved, literals passed through. */
export function resolveValue(value: string | undefined, theme: Theme, fallback: string): string {
  if (value === undefined || value === null || value === "") return fallback;
  if (isTokenRef(value)) return resolveToken(value, theme) ?? fallback;
  return value;
}

export interface ThemeTokenInfo {
  token: `$${string}`;
  label: string;
  value: string;
  group: "colors" | "typography" | "table" | "brand";
}

/** Every token a theme exposes, for pickers in the editor. */
export function listThemeTokens(theme: Theme): ThemeTokenInfo[] {
  const merged: Theme = mergeTheme(theme);
  const out: ThemeTokenInfo[] = [];
  for (const [k, v] of Object.entries(merged.colors)) if (typeof v === "string") out.push({ token: `$colors.${k}`, label: humanize(k), value: v, group: "colors" });
  for (const [k, v] of Object.entries(merged.typography)) if (typeof v === "string") out.push({ token: `$typography.${k}`, label: humanize(k), value: v, group: "typography" });
  for (const [k, v] of Object.entries(merged.table ?? {})) if (typeof v === "string") out.push({ token: `$table.${k}`, label: humanize(k), value: v, group: "table" });
  if (merged.logo) out.push({ token: "$logo", label: "Logo", value: merged.logo, group: "brand" });
  if (merged.logoDark) out.push({ token: "$logoDark", label: "Logo (dark)", value: merged.logoDark, group: "brand" });
  return out;
}

export function humanize(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
}

/** Merges a partial host theme over the default theme so every value is present. */
export function mergeTheme(theme: Partial<Theme> | undefined): Theme {
  if (!theme) return DEFAULT_THEME;
  return {
    ...DEFAULT_THEME,
    ...theme,
    typography: { ...DEFAULT_THEME.typography, ...(theme.typography ?? {}) },
    colors: { ...DEFAULT_THEME.colors, ...(theme.colors ?? {}), chart: theme.colors?.chart?.length ? theme.colors.chart : DEFAULT_THEME.colors.chart },
    table: { ...DEFAULT_THEME.table, ...(theme.table ?? {}) },
    fonts: theme.fonts ?? [],
    meta: theme.meta ?? {}
  };
}

/** Reports which theme values the host left undefined (validation warning source). */
export function missingThemeValues(theme: Partial<Theme>): string[] {
  const missing: string[] = [];
  const required: Array<keyof Theme["colors"]> = ["primary", "secondary", "accent", "text", "mutedText", "surface", "background", "border"];
  for (const k of required) if (!theme.colors?.[k]) missing.push(`colors.${k}`);
  if (!theme.typography?.headingFont) missing.push("typography.headingFont");
  if (!theme.typography?.bodyFont) missing.push("typography.bodyFont");
  return missing;
}

/** Simple relative-luminance check used to pick readable text on coloured surfaces. */
export function isDarkColor(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const lum = 0.2126 * srgb(r) + 0.7152 * srgb(g) + 0.0722 * srgb(b);
  return lum < 0.4;
}

function srgb(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/**
 * What to call a text style where a person will read it.
 *
 * The ids are the contract — `h1` is what a template stores and what a block
 * references — but nobody thinks in `h1`. The inspector already spelled these
 * out inline in one place; naming them once means the style dropdown, the
 * document panel and the block inspector cannot drift apart. An id the built-in
 * set does not know about is humanised rather than guessed at, so a host that
 * adds `pullQuote` gets "Pull quote" instead of "Heading ullQuote".
 */
const TEXT_STYLE_NAMES: Record<string, string> = {
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  h5: "Heading 5",
  h6: "Heading 6",
  body: "Body text",
  lead: "Lead paragraph",
  caption: "Caption",
  small: "Small print"
};

export function textStyleLabel(id: string): string {
  return TEXT_STYLE_NAMES[id] ?? humanize(id);
}
